import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

// Matches SUPPLIER_TYPES in SupplierCard.jsx — kept as a literal list here
// for the same reason categories is: no shared constants module currently
// exists between these two frontend files.
const SUPPLIER_TYPES = ['Supplies', 'Services', 'Products', 'Payroll'];

// FIX (15 July 2026): Derives a cleaner default supplier name from a raw
// bank description by cutting the string at the first run of 6+
// consecutive digits (account numbers, sort-code-prefixed references,
// transaction IDs). Bank exports like Starling's "Counter Party" field
// often concatenate the real payee name with a long reference string
// (e.g. "1979 LIMITED 400000001802042077 EMMA HALL 309897..."), and
// everything after that digit run is reference noise, not part of the
// payee name. Without this, unrelated suppliers ended up with the
// account holder's own name baked into their auto-generated supplier
// name (e.g. "1979 LIMITED ... EMMA HALL ...", "KATIE HUGHES ... EMMA
// HALL HAIR ...") — confirmed against real data on Andrew's account.
//
// This is a default SUGGESTION only — txn.description itself is left
// completely untouched (it's still what BankCategoryMapping matches
// against and what's shown in the row for reference), and the Supplier
// Name field stays fully editable, so a false trim just costs one extra
// click to fix rather than silently creating the wrong supplier.
const deriveDefaultSupplierName = (description) => {
  if (!description) return '';
  const cutIndex = description.search(/\s\d{6,}/);
  const cleaned = cutIndex > -1 ? description.slice(0, cutIndex) : description;
  // patch119b: same cleanup as UnmatchedTransactions (card suffix, dates, SP/SQSP prefixes, references)
  const tidy = cleaned
    .replace(/^(?:SQSP\*|SP\s+|PP\*)\s*/i, '')
    .replace(/\s+CD\s+\d{4}\b.*$/i, '')
    .replace(/\s*#\d+.*$/, '')
    .replace(/\d{6,}.*$/, '')
    .trim();
  return tidy || cleaned.trim();
};

const BankImportInterface = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [bankName, setBankName] = useState('anna');
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  // NEW: returned by /bank/upload-csv, needed so Cancel can identify which
  // rows belong to this specific import and soft-delete only those.
  const [importBatchId, setImportBatchId] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    matched: 0,
    suggested: 0,
    unmatched: 0
  });
  const [reviewedTransactions, setReviewedTransactions] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState({
    matched: true,
    suggested: true,
    unmatched: true
  });

  const steps = ['Upload CSV', 'Review Matches', 'Complete Import'];

  // Categories list — kept in sync with SUPPLIER_CATEGORIES (the canonical
  // category enum used by ExpenseManagement.jsx and the calculator service).
  // Patch 80: these categories never have a bill.
  const NO_BILL_CATEGORIES = ['Internal Transfer', 'Personal', 'Taxation', 'Loan Repayment', 'Bank Charges & Interest', 'Payroll', 'PAYE & NI'];
  const categories = [
    'Accountancy & Legal',
    'Bank Charges & Interest',
    'Card Machine Fees',
    'Client Comfort',
    'Communications',
    'Electric',
    'Furniture & Equipment',
    'Gas',
    'Insurance',
    'Internal Transfer',
    'IT & Software',
    'Leasing and Finance',
    'Licences',
    'Loan Repayment',
    'Maintenance & Repairs',
    'Marketing & Advertising',
    'Office Supplies',
    'Payroll',
    'PAYE & NI',
    'Personal',
    'Postage and Courier',
    'Rates',
    'Rent',
    'Staff Welfare',
    'Stock',
    'Subscriptions',
    'Sundry',
    'Taxation',
    'Training',
    'Utilities',
    'Waste Disposal',
    'Water'
  ];

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a valid CSV file');
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop a valid CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bank_name', bankName);

      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/bills/bank/upload-csv', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const newBatchId = response.data.import_batch_id || null;
        setImportBatchId(newBatchId);
        setImportSummary({
          ...(response.data.summary || {}),
          skipped_rows: response.data.skipped_rows || [],
          rejected_rows: response.data.rejected_rows || [],
        });
        setShowSkipped(false);
        setShowRejected(false);

        // Pass the new batch id explicitly: the state set above has not
        // been applied yet, so loadTransactions() would otherwise see null.
        await loadTransactions(newBatchId);
        setActiveStep(1);
      }
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (batchId = importBatchId) => {
    try {
      const token = localStorage.getItem('access_token');

      const txnResponse = await axios.get('/api/bills/bank/transactions/unmatched', {
        params: { import_batch_id: batchId },
        headers: { Authorization: `Bearer ${token}` }
      });

      const unmatchedTxns = txnResponse.data.transactions || [];

      const transactionsWithSuggestions = await Promise.all(
        unmatchedTxns.map(async (txn) => {
          try {
            const suggestResponse = await axios.get(
              `/api/bills/bank/match-suggestions/${txn.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            const suggestions = suggestResponse.data.suggestions || [];

            return {
              ...txn,
              suggestions,
              confidence: suggestions.length > 0 ? suggestions[0].confidence : 'none',
              bestMatch: suggestions.length > 0 ? suggestions[0] : null,
              selectedCategory: txn.confirmed_category || null,
              learnCategory: false,
              categorySaving: false,
              categorySaved: !!txn.confirmed_category,
              isNewSupplierBill: false,
              billSupplierName: deriveDefaultSupplierName(txn.description),
              billSupplierType: 'Supplies',
              creatingBill: false,
              // NEW (16 July 2026 — Item 4.1): fuzzy-match-against-existing-
              // suppliers state for the "Create a bill" panel.
              // supplierMatch: result of /bank/supplier-match-suggestion,
              //   or null if not checked yet.
              // supplierMatchChecking: true while the lookup is in flight.
              // supplierMatchDecision: 'use_existing' | 'confirmed_new' | null
              //   — null means the suggestion (if any) hasn't been
              //   resolved yet, so "Create Bill" stays disabled if there
              //   IS a pending suggestion. If supplierMatch comes back with
              //   match_type: null, there's nothing to resolve and the
              //   user can create straight away, same as before this fix.
              supplierMatch: null,
              supplierMatchChecking: false,
              supplierMatchDecision: null
            };
          } catch {
            return {
              ...txn,
              suggestions: [],
              confidence: 'none',
              bestMatch: null,
              selectedCategory: txn.confirmed_category || null,
              learnCategory: false,
              categorySaving: false,
              categorySaved: !!txn.confirmed_category,
              isNewSupplierBill: false,
              billSupplierName: '',
              billSupplierType: 'Supplies',
              creatingBill: false,
              supplierMatch: null,
              supplierMatchChecking: false,
              supplierMatchDecision: null
            };
          }
        })
      );

      setTransactions(transactionsWithSuggestions);
      setReviewedTransactions(prev => {
        const next = new Set(prev);
        transactionsWithSuggestions.forEach(t => {
          if (t.confirmed_category) next.add(t.id);
        });
        return next;
      });

      const matched = transactionsWithSuggestions.filter(t => t.confidence === 'high' && t.bestMatch).length;
      const suggested = transactionsWithSuggestions.filter(t => t.confidence === 'medium').length;
      const unmatched = transactionsWithSuggestions.filter(t => t.confidence === 'none').length;

      setStats({
        total: transactionsWithSuggestions.length,
        matched,
        suggested,
        unmatched
      });
    } catch (err) {
      setError('Failed to load transactions: ' + (err.response?.data?.error || err.message));
    }
  };

  const confirmMatch = async (transactionId, billId) => {
    const txn = transactions.find(t => t.id === transactionId);
    const wasHighConfidence = txn && txn.confidence === 'high';

    setTransactions(prev =>
      prev.map(t => (t.id === transactionId ? { ...t, confirming: true } : t))
    );

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/bank/match',
        {
          transaction_id: transactionId,
          bill_id: billId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      setReviewedTransactions(prev => new Set([...prev, transactionId]));

      setStats(prev => ({
        ...prev,
        matched: prev.matched + 1,
        suggested: wasHighConfidence ? prev.suggested : prev.suggested - 1,
        total: prev.total - 1
      }));
    } catch (err) {
      setTransactions(prev =>
        prev.map(t => (t.id === transactionId ? { ...t, confirming: false } : t))
      );
      setError('Failed to confirm match: ' + (err.response?.data?.error || err.message));
    }
  };

  const rejectMatch = (transactionId) => {
    const txn = transactions.find(t => t.id === transactionId);
    const wasHighConfidence = txn && txn.confidence === 'high';

    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId
          ? { ...t, confidence: 'none', bestMatch: null }
          : t
      )
    );

    setStats(prev => ({
      ...prev,
      matched: wasHighConfidence ? prev.matched - 1 : prev.matched,
      suggested: wasHighConfidence ? prev.suggested : prev.suggested - 1,
      unmatched: prev.unmatched + 1
    }));
  };

  const handleCategoryChange = async (transactionId, category, learnOverride, billWantedOverride) => {
    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId
          ? { ...t, selectedCategory: category, categorySaving: true, categorySaved: false, isNewSupplierBill: NO_BILL_CATEGORIES.includes(category) ? false : t.isNewSupplierBill }
          : t
      )
    );
    setReviewedTransactions(prev => new Set([...prev, transactionId]));

    const txn = transactions.find(t => t.id === transactionId);
    const learn = learnOverride !== undefined ? learnOverride : (txn ? txn.learnCategory : false);
    const billWanted = billWantedOverride !== undefined ? billWantedOverride : (txn ? !!txn.isNewSupplierBill : false);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/bank/transactions/categorize',
        {
          transaction_id: transactionId,
          category_name: category,
          learn_category: learn,
          create_bill_wanted: billWanted
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, categorySaving: false, categorySaved: true }
            : t
        )
      );
    } catch (err) {
      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, categorySaving: false, categorySaved: false }
            : t
        )
      );
      setError(
        `Failed to save category for "${txn ? txn.description : 'transaction'}": ` +
        (err.response?.data?.error || err.message)
      );
    }
  };

  const handleLearnCategory = (transactionId, learn) => {
    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId
          ? { ...t, learnCategory: learn }
          : t
      )
    );
    const txn = transactions.find(t => t.id === transactionId);
    if (txn && txn.selectedCategory) {
      handleCategoryChange(transactionId, txn.selectedCategory, learn);
    }
  };

  // NEW (16 July 2026 — Item 4.1): calls /bank/supplier-match-suggestion
  // for the current billSupplierName on this row. Deliberately NOT
  // auto-applied — this only sets supplierMatch so the UI can render the
  // Yes / No it's different / Create new choice. Nothing about the
  // supplier or bill changes until the user picks one of those options.
  const checkSupplierMatch = async (transactionId, nameOverride) => {
    const txn = transactions.find(t => t.id === transactionId);
    const nameToCheck = (nameOverride !== undefined ? nameOverride : txn?.billSupplierName || '').trim();

    if (!nameToCheck) {
      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, supplierMatch: null, supplierMatchDecision: null }
            : t
        )
      );
      return;
    }

    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId
          ? { ...t, supplierMatchChecking: true, supplierMatchDecision: null }
          : t
      )
    );

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/bills/bank/supplier-match-suggestion', {
        params: { description: nameToCheck },
        headers: { Authorization: `Bearer ${token}` }
      });

      const suggestion = response.data;
      const hasSuggestion = suggestion && suggestion.match_type;

      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? {
                ...t,
                supplierMatch: hasSuggestion ? suggestion : null,
                supplierMatchChecking: false,
                // No suggestion at all means there's nothing to resolve —
                // treat as already "confirmed new" so Create Bill isn't
                // blocked waiting on a decision that was never offered.
                supplierMatchDecision: hasSuggestion ? null : 'confirmed_new'
              }
            : t
        )
      );
    } catch (err) {
      // Lookup failing shouldn't block the user from creating the bill —
      // fall back to the pre-4.1 behaviour (free-text name, no proposal)
      // rather than leaving Create Bill permanently disabled.
      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, supplierMatch: null, supplierMatchChecking: false, supplierMatchDecision: 'confirmed_new' }
            : t
        )
      );
    }
  };

  // NEW: user clicked "Yes, use this supplier" on a proposed match —
  // swap the draft supplier name to the matched supplier's real name and
  // mark the decision resolved, ready to create.
  const acceptSuggestedSupplier = (transactionId) => {
    setTransactions(prev =>
      prev.map(t => {
        if (t.id !== transactionId || !t.supplierMatch) return t;
        return {
          ...t,
          billSupplierName: t.supplierMatch.supplier_name,
          supplierMatchDecision: 'use_existing'
        };
      })
    );
  };

  // NEW: user clicked "No, it's different" — keep their typed name,
  // dismiss the suggestion, and let them proceed as a genuinely new
  // supplier.
  const declineSuggestedSupplier = (transactionId) => {
    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId
          ? { ...t, supplierMatchDecision: 'confirmed_new' }
          : t
      )
    );
  };

   const toggleNewSupplierBillMode = (transactionId, isBill) => {
    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId ? { ...t, isNewSupplierBill: isBill } : t
      )
    );
    // NEW: as soon as the panel opens, check the pre-filled name against
    // existing suppliers, rather than waiting for the user to touch
    // anything — the pre-filled name (from deriveDefaultSupplierName) is
    // already a real candidate worth checking immediately.
    if (isBill) {
      checkSupplierMatch(transactionId);
    }
    // NEW: ticking/unticking this checkbox changes what bill_required
    // should be on the backend. If a category's already been saved for
    // this row, re-send it now with the updated checkbox state so
    // bill_required stays in sync — otherwise it keeps whatever value
    // was sent the last time handleCategoryChange ran, which could be
    // stale (e.g. category saved with the box unticked, then ticked
    // afterwards, but the backend never told).
    const txn = transactions.find(t => t.id === transactionId);
    if (txn && txn.selectedCategory) {
      handleCategoryChange(transactionId, txn.selectedCategory, txn.learnCategory, isBill);
    }
  };

  const updateBillDraftField = (transactionId, field, value) => {
    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId ? { ...t, [field]: value } : t
      )
    );
    // NEW: editing the supplier name invalidates any prior match decision —
    // re-check once the user finishes typing (see TextField's onBlur below;
    // this just clears state so stale "matched" UI doesn't linger while
    // they're still typing).
    if (field === 'billSupplierName') {
      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, supplierMatch: null, supplierMatchDecision: null }
            : t
        )
      );
    }
  };

  const createBillFromTransaction = async (transactionId) => {
    const txn = transactions.find(t => t.id === transactionId);
    if (!txn || !txn.billSupplierName || !txn.billSupplierName.trim()) {
      setError('Please enter a supplier name before creating a bill.');
      return;
    }

    setTransactions(prev =>
      prev.map(t => (t.id === transactionId ? { ...t, creatingBill: true } : t))
    );

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/bank/create-bill-from-transaction',
        {
          transaction_id: transactionId,
          supplier_name: txn.billSupplierName.trim(),
          supplier_type: txn.billSupplierType || 'Supplies',
          category_name: txn.selectedCategory || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      setReviewedTransactions(prev => new Set([...prev, transactionId]));
      setStats(prev => ({
        ...prev,
        matched: prev.matched + 1,
        unmatched: prev.unmatched - 1,
        total: prev.total - 1
      }));
    } catch (err) {
      setTransactions(prev =>
        prev.map(t => (t.id === transactionId ? { ...t, creatingBill: false } : t))
      );
      setError('Failed to create bill: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCancel = async () => {
    if (!importBatchId) {
      setActiveStep(0);
      return;
    }

    setCancelling(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const totalInBatchBeforeCancel = stats.total;

      const response = await axios.post(
        '/api/bills/bank/cancel-import',
        { import_batch_id: importBatchId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const cancelledCount = response.data.rows_cancelled || 0;
      const keptCount = Math.max(totalInBatchBeforeCancel - cancelledCount, 0);

      setTransactions([]);
      setReviewedTransactions(new Set());
      setStats({ total: 0, matched: 0, suggested: 0, unmatched: 0 });
      setImportBatchId(null);
      setImportSummary(null);
      setFile(null);
      setActiveStep(0);

      if (keptCount > 0) {
        setError(
          `Import cancelled: ${cancelledCount} transaction(s) removed. ` +
          `${keptCount} transaction(s) in this import were already matched to bills and kept, since undoing those would also remove the associated payment.`
        );
      } else {
        setError(`Import cancelled: ${cancelledCount} transaction(s) removed.`);
      }
    } catch (err) {
      setError('Failed to cancel import: ' + (err.response?.data?.error || err.message));
    } finally {
      setCancelling(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const matchedTransactions = transactions.filter(t => t.confidence === 'high' && t.bestMatch);
  const suggestedTransactions = transactions.filter(t => t.confidence === 'medium');
  const unmatchedTransactions = transactions.filter(t => t.confidence === 'none');

  const needsReview = matchedTransactions.length + suggestedTransactions.length + unmatchedTransactions.length;
  const canComplete = reviewedTransactions.size >= needsReview && transactions.length > 0;

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Import Bank Statement
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Import transactions from your bank and match them to unpaid bills
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && (
        <Card>
          <CardContent>
            <Box
              sx={{
                border: '3px dashed',
                borderColor: 'primary.main',
                borderRadius: 2,
                p: 8,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => document.getElementById('file-upload').click()}
            >
              <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {file ? file.name : 'Drop your bank statement CSV here'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to browse
              </Typography>
              <input
                id="file-upload"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Bank</InputLabel>
                <Select
                  value={bankName}
                  label="Bank"
                  onChange={(e) => setBankName(e.target.value)}
                >
                  <MenuItem value="anna">ANNA</MenuItem>
                  <MenuItem value="starling">Starling Bank</MenuItem>
                  <MenuItem value="bank_of_scotland">Bank of Scotland</MenuItem>
                  <MenuItem value="hsbc" disabled>HSBC (coming soon)</MenuItem>
                  <MenuItem value="barclays" disabled>Barclays (coming soon)</MenuItem>
                  <MenuItem value="other" disabled>Other (coming soon)</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!file || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
              >
                {loading ? 'Processing...' : 'Upload & Process'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeStep === 1 && (
        <>
          {importSummary && (() => {
            const s = importSummary;
            const accounted = (s.created || 0) + (s.skipped_existing || 0)
              + (s.rejected || 0) + (s.ignored_not_spend || 0);
            const addsUp = accounted === s.rows_in_file;
            return (
              <Alert severity={s.rejected > 0 || !addsUp ? 'warning' : 'info'} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  File had {s.rows_in_file} rows: {s.created} created, {s.skipped_existing} already in the system,
                  {' '}{s.rejected} rejected, {s.ignored_not_spend} money-in rows ignored.
                  {addsUp ? ' All rows accounted for.' : ` Totals differ by ${s.rows_in_file - accounted}.`}
                </Typography>
                {s.created_with_category > 0 && (
                  <Typography variant="body2">
                    Of the {s.created} created, {s.created_with_category} were categorised automatically from
                    learned rules and are not listed below. Cancel removes all {s.created}, including those.
                  </Typography>
                )}
                {s.skipped_rows.length > 0 && (
                  <Button size="small" onClick={() => setShowSkipped(v => !v)}>
                    {showSkipped ? 'Hide' : 'View'} {s.skipped_rows.length} skipped rows
                  </Button>
                )}
                {s.rejected_rows.length > 0 && (
                  <Button size="small" color="warning" onClick={() => setShowRejected(v => !v)}>
                    {showRejected ? 'Hide' : 'View'} {s.rejected_rows.length} rejected rows
                  </Button>
                )}
                <Collapse in={showSkipped}>
                  {s.skipped_rows.map((r, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {r.date} · {r.description} · {r.reference || '—'} · £{r.amount}
                    </Typography>
                  ))}
                </Collapse>
                <Collapse in={showRejected}>
                  {s.rejected_rows.map((r, i) => (
                    <Typography key={i} variant="caption" display="block" color="error">
                      {r.raw} — {r.error}
                    </Typography>
                  ))}
                </Collapse>
              </Alert>
            );
          })()}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" variant="body2">
                    Total Transactions
                  </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent>
                  <Typography color="textSecondary" variant="body2">
                    Auto-Matched
                  </Typography>
                  <Typography variant="h4" color="success.dark">
                    {stats.matched}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card sx={{ bgcolor: 'warning.light' }}>
                <CardContent>
                  <Typography color="textSecondary" variant="body2">
                    Need Review
                  </Typography>
                  <Typography variant="h4" color="warning.dark">
                    {stats.suggested}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card sx={{ bgcolor: 'info.light' }}>
                <CardContent>
                  <Typography color="textSecondary" variant="body2">
                    Unmatched
                  </Typography>
                  <Typography variant="h4" color="info.dark">
                    {stats.unmatched}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {matchedTransactions.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => toggleSection('matched')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="success" />
                    <Typography variant="h6">High-Confidence Matches — Please Confirm</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={matchedTransactions.length} color="success" size="small" />
                    <IconButton size="small">
                      {expandedSections.matched ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedSections.matched}>
                  <Box sx={{ mt: 2 }}>
                    {matchedTransactions.map((txn) => (
                      <Paper key={txn.id} sx={{ p: 2, mb: 1, bgcolor: 'success.50' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={2}>
                            <Typography variant="body2" fontWeight="bold">
                              {formatDate(txn.transaction_date)}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography>{txn.description}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {txn.reference}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="h6" align="right">
                              {formatCurrency(txn.amount)}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Alert severity="success" icon={<CheckIcon />}>
                              Matches Bill <strong>{txn.bestMatch.invoice_number}</strong>
                            </Alert>
                          </Grid>
                          <Grid item xs={2}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={txn.confirming}
                                onClick={() => confirmMatch(txn.id, txn.bestMatch.bill_id)}
                              >
                                {txn.confirming ? <CircularProgress size={16} /> : 'Confirm'}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => rejectMatch(txn.id)}
                              >
                                Not a Match
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {suggestedTransactions.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => toggleSection('suggested')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="h6">Suggested Matches - Please Review</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={suggestedTransactions.length} color="warning" size="small" />
                    <IconButton size="small">
                      {expandedSections.suggested ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedSections.suggested}>
                  <Box sx={{ mt: 2 }}>
                    {suggestedTransactions.map((txn) => (
                      <Paper key={txn.id} sx={{ p: 2, mb: 1, bgcolor: 'warning.50' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={2}>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDate(txn.transaction_date)}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography>{txn.description}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {txn.reference}
                                </Typography>
                              </Grid>
                              <Grid item xs={2}>
                                <Typography variant="h6" align="right">
                                  {formatCurrency(txn.amount)}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Chip label="Review Required" color="warning" size="small" />
                              </Grid>
                            </Grid>
                          </Grid>

                          {txn.bestMatch && (
                            <Grid item xs={12}>
                              <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Possible match:</strong> Bill {txn.bestMatch.invoice_number}
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  <Grid item xs={4}>
                                    <Typography variant="caption">Bill Amount</Typography>
                                    <Typography>{formatCurrency(txn.bestMatch.amount)}</Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption">Transaction</Typography>
                                    <Typography>{formatCurrency(txn.amount)}</Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption">Difference</Typography>
                                    <Typography>{formatCurrency(Math.abs(txn.bestMatch.amount - txn.amount))}</Typography>
                                  </Grid>
                                </Grid>
                              </Alert>

                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => confirmMatch(txn.id, txn.bestMatch.bill_id)}
                                >
                                  ✓ Confirm Match
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => rejectMatch(txn.id)}
                                >
                                  ✗ Not a Match
                                </Button>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {unmatchedTransactions.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => toggleSection('unmatched')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="info" />
                    <Typography variant="h6">New Expenses - Need Categorization</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={unmatchedTransactions.length} color="info" size="small" />
                    <IconButton size="small">
                      {expandedSections.unmatched ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedSections.unmatched}>
                  <Box sx={{ mt: 2 }}>
                    {unmatchedTransactions.map((txn) => (
                      <Paper key={txn.id} sx={{ p: 2, mb: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={2}>
                            <Typography variant="body2" fontWeight="bold">
                              {formatDate(txn.transaction_date)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography>{txn.description}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {txn.reference}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="h6" align="right">
                              {formatCurrency(txn.amount)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Category</InputLabel>
                              <Select
                                value={txn.selectedCategory || ''}
                                label="Category"
                                onChange={(e) => handleCategoryChange(txn.id, e.target.value)}
                              >
                                {categories.map((cat) => (
                                  <MenuItem key={cat} value={cat}>
                                    {cat}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {txn.categorySaving && (
                              <Typography variant="caption" color="text.secondary">
                                Saving…
                              </Typography>
                            )}
                            {!txn.categorySaving && txn.categorySaved && (
                              <Typography variant="caption" color="success.main">
                                ✓ Saved
                              </Typography>
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={txn.learnCategory}
                                  onChange={(e) => handleLearnCategory(txn.id, e.target.checked)}
                                />
                              }
                              label={
                                <Typography variant="caption" title="Matches this exact payee text next time. Only useful where the text is identical every time, not where it includes a changing code.">
                                  Remember "{txn.description}" = this category
                                </Typography>
                              }
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <FormControlLabel
                              sx={{ display: NO_BILL_CATEGORIES.includes(txn.selectedCategory) ? 'none' : undefined }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={txn.isNewSupplierBill}
                                  onChange={(e) => toggleNewSupplierBillMode(txn.id, e.target.checked)}
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  Create a bill for this transaction
                                </Typography>
                              }
                            />

                            <Collapse in={txn.isNewSupplierBill}>
                              <Box sx={{ mt: 1, pl: 4 }}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} sm={5}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Supplier Name"
                                      value={txn.billSupplierName}
                                      onChange={(e) =>
                                        updateBillDraftField(txn.id, 'billSupplierName', e.target.value)
                                      }
                                      // NEW: re-check for a fuzzy match once the
                                      // user finishes editing the name, rather
                                      // than firing a request on every keystroke.
                                      onBlur={() => checkSupplierMatch(txn.id)} helperText="If SalonLogic suggests an existing supplier, press Yes. Only type a new name for a business you have never paid before."
                                      required
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Supplier Type</InputLabel>
                                      <Select
                                        value={txn.billSupplierType}
                                        label="Supplier Type"
                                        onChange={(e) =>
                                          updateBillDraftField(txn.id, 'billSupplierType', e.target.value)
                                        }
                                      >
                                        {SUPPLIER_TYPES.map((t) => (
                                          <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      size="small"
                                      disabled={
                                        txn.creatingBill ||
                                        !txn.billSupplierName.trim() ||
                                        txn.supplierMatchChecking ||
                                        // Block "Create Bill" while a real
                                        // suggestion is sitting unresolved —
                                        // the user must click Yes/No below
                                        // first. If there was never a
                                        // suggestion (or the lookup failed),
                                        // supplierMatchDecision is already
                                        // 'confirmed_new' and this doesn't
                                        // block anything.
                                        (txn.supplierMatch && !txn.supplierMatchDecision)
                                      }
                                      onClick={() => createBillFromTransaction(txn.id)}
                                      startIcon={txn.creatingBill ? <CircularProgress size={16} /> : null}
                                    >
                                      {txn.creatingBill ? 'Creating…' : 'Create Bill'}
                                    </Button>
                                  </Grid>
                                </Grid>

                                {/* NEW (16 July 2026 — Item 4.1): propose/confirm
                                    panel. Shown only while a suggestion is
                                    unresolved for the CURRENT billSupplierName. */}
                                {txn.supplierMatchChecking && (
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={14} />
                                    <Typography variant="caption" color="text.secondary">
                                      Checking existing suppliers…
                                    </Typography>
                                  </Box>
                                )}

                                {txn.supplierMatch && !txn.supplierMatchDecision && (
                                  <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="body2" gutterBottom>
                                      {txn.supplierMatch.match_type === 'alias' ? (
                                        <>This looks like <strong>{txn.supplierMatch.supplier_name}</strong> (known alias) — use this supplier?</>
                                      ) : (
                                        <>This looks like <strong>{txn.supplierMatch.supplier_name}</strong> ({Math.round(txn.supplierMatch.similarity * 100)}% match) — use this supplier?</>
                                      )}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => acceptSuggestedSupplier(txn.id)}
                                      >
                                        Yes, use this supplier
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => declineSuggestedSupplier(txn.id)}
                                      >
                                        No, it's different
                                      </Button>
                                    </Box>
                                  </Alert>
                                )}

                                {txn.supplierMatchDecision === 'use_existing' && (
                                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                                    ✓ Will attach this bill to the existing supplier "{txn.billSupplierName}".
                                  </Typography>
                                )}

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  VAT is calculated at 20% (one-sixth of the payment) for suppliers marked VAT registered on the Supplier Card. Other suppliers get no VAT. This page cannot enter a different rate. If your invoice shows 5%, nil VAT or a mix, leave Create a bill unticked and ask your accountant. Confirm the supplier's status on the Supplier Card afterwards.
                                </Typography>
                              </Box>
                            </Collapse>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          <Paper sx={{ p: 2, position: 'sticky', bottom: 20, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                <strong>{reviewedTransactions.size}</strong> of <strong>{needsReview}</strong> reviewed
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setConfirmCancelOpen(true)}
                  disabled={cancelling}
                  startIcon={cancelling ? <CircularProgress size={16} /> : null}
                >
                  {cancelling ? 'Cancelling…' : 'Cancel'}
                </Button>
                <Button
                  variant="contained"
                  disabled={!canComplete}
                  onClick={() => setActiveStep(2)}
                >
                  Complete Import ({stats.total} transactions)
                </Button>
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {activeStep === 2 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Import Complete!
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Successfully imported {stats.total} transactions
            </Typography>
            <Button variant="contained" onClick={() => window.location.href = '/supplier_card'}>
              View Supplier Bills
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)}>
        <DialogTitle>Cancel this import?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes all {importSummary?.created ?? stats.total} transactions created by this upload,
            including {importSummary?.created_with_category ?? 0} that were categorised automatically and
            are not shown on this screen. Rows already matched to bills are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelOpen(false)}>Keep import</Button>
          <Button color="error" onClick={() => { setConfirmCancelOpen(false); handleCancel(); }}>
            Cancel import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankImportInterface;